/**
 * StaffProfileSettings — V1.8.6
 *
 * `/settings/profile` — every staff role's personal profile page.
 * Currently exposes: avatar upload. Read-only name, email, role,
 * workspace so users can confirm who they're signed in as.
 *
 * Backend endpoint: POST/DELETE /me/profile/avatar (works for all roles).
 */
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AvatarUploader from "@/components/AvatarUploader";
import PasskeyManager from "@/components/PasskeyManager";
import { ArrowLeft, ShieldCheck, EnvelopeSimple, User } from "@phosphor-icons/react";

function initialsOf(name) {
    if (!name) return "?";
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
}

export default function StaffProfileSettings() {
    const { user, setUser } = useAuth();
    const initials = initialsOf(user?.name);

    return (
        <div className="min-h-screen bg-paper py-8 px-4" data-testid="staff-profile-page">
            <div className="max-w-2xl mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-ink/60 hover:text-cobalt mb-6"
                    data-testid="staff-profile-back"
                >
                    <ArrowLeft size={12} weight="bold" /> Back
                </Link>

                <div className="mb-8">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-2">
                        Your profile
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-ink">
                        Profile photo &amp; details
                    </h1>
                    <p className="text-sm text-ink/60 mt-2 max-w-lg">
                        Your photo shows up on chat bubbles, lock-screen notifications, group chats,
                        and the candidate-facing DaddyTalk drawer. Adding one makes hiring feel human.
                    </p>
                </div>

                <div className="bg-white border border-ink/10 rounded-2xl p-6 sm:p-8 shadow-sm">
                    <div className="flex flex-col items-center pb-6 border-b border-ink/10">
                        <AvatarUploader
                            initialUrl={user?.avatar_url}
                            initials={initials}
                            size="lg"
                            variant="cobalt"
                            testIdPrefix="staff-avatar"
                            onChange={(url) => setUser({ ...user, avatar_url: url })}
                        />
                    </div>

                    <div className="mt-6 space-y-4">
                        <ReadonlyRow icon={User} label="Name" value={user?.name} testId="staff-profile-name" />
                        <ReadonlyRow icon={EnvelopeSimple} label="Email" value={user?.email} testId="staff-profile-email" />
                        <ReadonlyRow
                            icon={ShieldCheck}
                            label="Role"
                            value={(user?.role || "").replace("_", " ")}
                            testId="staff-profile-role"
                            valueClass="capitalize"
                        />
                    </div>

                    <p className="mt-6 text-[11px] text-ink/50">
                        To change your name or email, contact your workspace admin.
                    </p>
                </div>

                <div className="mt-6">
                    <PasskeyManager />
                </div>
            </div>
        </div>
    );
}

function ReadonlyRow({ icon: Icon, label, value, testId, valueClass = "" }) {
    return (
        <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-ink/5 flex items-center justify-center shrink-0">
                <Icon size={16} weight="bold" className="text-ink/60" />
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-wider text-ink/45">{label}</p>
                <p className={`text-sm font-semibold text-ink truncate ${valueClass}`} data-testid={testId}>
                    {value || "—"}
                </p>
            </div>
        </div>
    );
}
